// ExportModal.tsx
import { Button, Modal, Select, Checkbox, Radio } from "antd";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

const ExportModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  
  .section {
    display: flex;
    gap: 8px;
    
    .section-title {
      font-weight: 500;
      margin-bottom: 8px;
    }
    
    .options-container {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
      .ant-checkbox-wrapper{
      margin-left:0
      }
  }
  
  .select-container {
    width: 200px;
    margin-left: 10px;
  }
`;

interface ExportModalProps {
    visible: boolean;
    onCancel: () => void;
    onOk: () => void;
    exportFormat: "PDF" | "XLSX";
    setExportFormat: (format: "PDF" | "XLSX") => void;
    exportLanguage: string;
    setExportLanguage: (language: string) => void;
    inventories: Array<{
        id: string;
        name: string;
    }>;
    selectedInventories: Record<string, boolean>;
    onInventorySelect: (inventoryId: string, checked: boolean) => void;
}

const ExportModal: React.FC<ExportModalProps> = ({
    visible,
    onCancel,
    onOk,
    exportFormat,
    setExportFormat,
    exportLanguage,
    setExportLanguage,
    inventories,
    selectedInventories,
    onInventorySelect,
}) => {
    const [filteredInventories, setFilteredInventories] = useState<any>([]);
    const [selectAll, setSelectAll] = useState(false);

    useEffect(() => {
        // Filter inventories whenever exportLanguage changes
        const filtered = inventories.filter((inv: any) => {
            if (exportLanguage === 'default') return true;
            return inv.transliterated_languages?.includes(exportLanguage);
        });
        setFilteredInventories(filtered);
    }, [exportLanguage, inventories]);

    useEffect(() => {
        // Check if all inventories are selected to update the "Select All" checkbox
        const allSelected = inventories.length > 0 &&
            inventories.every(inventory => selectedInventories[inventory.id]);
        setSelectAll(allSelected);
    }, [selectedInventories, inventories]);

    const handleSelectAll = (e: any) => {
        const isChecked = e.target.checked;
        setSelectAll(isChecked);
        // Select or deselect all inventories
        inventories.forEach(inventory => {
            onInventorySelect(inventory.id, isChecked);
        });
    };

    return (
        <Modal
            title="Export Sankalp Sheet"
            visible={visible}
            onOk={onOk}
            onCancel={onCancel}
            width={800}
            footer={[
                <Button key="back" onClick={onCancel}>
                    Cancel
                </Button>,
                <Button key="submit" type="primary" onClick={onOk}>
                    Export
                </Button>,
            ]}
            bodyStyle={{
                maxHeight: '60vh',  // Set your desired height here
                overflowY: 'auto' // Add scroll if content exceeds height
            }}
        >
            <ExportModalContent>
                <div className="section">
                    <div className="section-title">In which format you want to export the sheet :</div>
                    <div className="options-container" style={{ marginLeft: "30px" }}>
                        <Radio.Group
                            onChange={(e) => setExportFormat(e.target.value)}
                            value={exportFormat}
                        >
                            <Radio value="PDF">PDF</Radio>
                            <Radio value="XLSX">CSV</Radio>
                        </Radio.Group>
                    </div>
                </div>

                <div className="section" style={{ display: 'flex', alignContent: 'center' }}>
                    <div className="section-title" style={{ marginTop: '4px' }}>In which Language you want to export the sheet:</div>
                    <div className="select-container">
                        <Select
                            value={exportLanguage}
                            onChange={(value) => setExportLanguage(value)}
                            options={[
                                { value: 'default', label: 'Default' },
                                { value: 'en', label: 'English' },
                                { value: 'hi', label: 'Hindi' },
                                { value: 'bn', label: 'Bengali' },
                                { value: 'ta', label: 'Tamil' },
                                { value: 'te', label: 'Telugu' },
                                { value: 'kn', label: 'Kannada' },
                            ]}
                        />
                    </div>
                </div>

                <div className="section" style={{ marginTop: "10px" }}>
                    <div className="options-container">
                        <Checkbox
                            checked={selectAll}
                            indeterminate={
                                !selectAll &&
                                inventories.some(inventory => selectedInventories[inventory.id])
                            }
                            onChange={handleSelectAll}
                        >
                            Select All
                        </Checkbox>
                        {inventories.map(inventory => (
                            <Checkbox
                                key={inventory.id}
                                checked={selectedInventories[inventory.id] || false}
                                onChange={(e) => onInventorySelect(inventory.id, e.target.checked)}
                            >
                                {inventory.name}
                            </Checkbox>
                        ))}
                    </div>
                </div>
            </ExportModalContent>
        </Modal>
    );
};

export default ExportModal;